import { Link, useNavigate } from "react-router";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import useAuth from "@/components/common/header/useAuth";
import UserDropDown from "@/components/common/header/UserDropDown";
import { setLoginModal, setLoginTab, setLoginCallLocation, setIsUserSubscribed, setShowContinueBtn } from "@/redux/login/loginSlice";
import { getAlertData, resetMessageCount } from "@/api/alerts";
import { setAlerts, setIsAlertsLoading, setProfileInfo } from "@/redux/user/userSlice";
import { getUserProfile } from "@/api/myProfile";
import { trackButtonClick, getEventCategory } from "@/utils/analytics";
import { handleSRPLogout } from "@/redux/SRP/srpSlice";
import { store } from "@/store/store";
import { getFullStateName } from "./utils";
import { getSavedSearchAndPropertyCount } from "@/api/savedSearch";
import { setSavedCount, showSavedTooltip } from "@/redux/savedpreference/savedPreferenceSlice";
import Alerts from "@/components/user/Alerts";
import SavedPreference from "@/components/common/header/save-preference";
import BidCenterHeader from "@/components/pdp/BidCenter/BidCenterHeader";
import { getPrice } from "@/components/pdp/BidCenter/bidCenterUtils";
import { getDHM } from "@/utils/dhmCalculation";
import { parseAutoExtendConfig } from "@/utils/autoExtendConfig";
import { getCookie } from "@/utils/cookies";
import SubscribedUser from "@/components/common/user-credentials/SubscribedUser";
import { CgBell } from "react-icons/cg";
import { ShimmerContentBlock } from "react-shimmer-effects";
import "./commercial-header.css";

/**
 * CommercialHeader - Custom header for Commercial PDP pages.
 * Matches the design: Logo | Buy · Sell · Blog · Alerts | "Sell an Asset Like This" CTA | User
 * Plus breadcrumbs row below.
 */
const CommercialHeader = ({ hideSellCTA = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isUserLoggedIn, userName, userLastName, userSignOut } = useAuth();
  const user = useSelector((state) => state.user);
  const alerts = useSelector((state) => state.user.alerts);
  const alertsCount = useSelector((state) => state.user.alertsCount);
  const loading = useSelector((state) => state?.user?.isAlertsLoading);
  const pdpInitData = useSelector((state) => state.pdp?.pdpInitData);
  const globalPopup = useSelector((state) => state.login);
  const savedCount = useSelector((state) => state.savedPreference);

  const [navbar, setNavbar] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSavedSidebarOpen, setIsSavedSidebarOpen] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showBidHeader, setShowBidHeader] = useState(false);
  const [currentTime, setCurrentTime] = useState(null);
  const [userSubscribed, setUserSubscribed] = useState(getCookie("subscribedUser") || false);
  const [showTooltip, setShowTooltip] = useState(false);

  const bidCenterData = useSelector((state) => state.pdp?.bidCenterData);
  const latestBidCenterData = useSelector((state) => state.pdp?.latestBidCenterData);
  const auctionDetails = useSelector((state) => state.pdp?.auctionDetails);

  const state = store.getState();
  const userID = state?.user?.user?.realbidUserId;

  const webCMS = useSelector(
    (state) => state.cms?.data?.global?.results?.[0]?.["shared"]?.["navLinks"]
  );

  // Breadcrumb data from PDP
  const propertyDetails = pdpInitData?.propertyDetails;
  const address = propertyDetails?.address;
  const commonAttributes = propertyDetails?.commonAttributes;
  const propCategory = commonAttributes?.propCategory;
  const propSubCategory = commonAttributes?.propSubCategory;
  const type = commonAttributes?.type;
  const propertyName = commonAttributes?.propertyName || pdpInitData?.propertySpecialDetails?.commercialPropertyAdditionalFeatures?.otherBuildingName;
  const stateName = address?.stateName || getFullStateName(address?.state);

  const buyDropDown = [
    { id: 1, name: webCMS?.realEstateAuctions || "All Real Estate Auctions", routerPath: "/auctions?listingType=AUCN" },
    { id: 2, name: webCMS?.bankOwned || "Bank Owned", routerPath: "/bank-owned" },
    { id: 3, name: webCMS?.signatureSellerProperties || "Hubzu Signature Homes", routerPath: "/auctions?sid=signature-seller" },
    { id: 4, name: webCMS?.offMarketProperties || "Off Market Properties", routerPath: "/auctions?sid=offmarket-properties" },
    { id: 5, name: webCMS?.commercialProperties || "Commercial Properties", routerPath: "/commercial" },
    { id: 6, name: webCMS?.CWCOTSecondChance || "CWCOT Second Chance", routerPath: "/auctions?listingType=AUCN&category=secondchance" },
    { id: 7, name: webCMS?.foreclosure || "Foreclosure", routerPath: "/foreclosures" },
    { id: 8, name: webCMS?.shortSale || "Short Sale", routerPath: "/short-sale" },
    { id: 9, name: webCMS?.traditionalSale || "Traditional Sale", routerPath: "/traditional-sale" },
  ];

  const sellDropDown = [
    { id: 1, name: "Residential Properties", routerPath: "/sellahomeinanonlineauction" },
    { id: 2, name: "Commercial Properties", routerPath: "/sell-commercial" },
  ];

  useEffect(() => {
    const changeBackground = () => {
      setNavbar(window.scrollY >= 95);
      // Show sticky bid bar after scrolling past the hero section (~500px)
      setShowBidHeader(window.scrollY >= 500);
    };
    window.addEventListener("scroll", changeBackground);
    return () => window.removeEventListener("scroll", changeBackground);
  }, []);

  // Alerts drawer events
  useEffect(() => {
    const alertsDrawer = document.getElementById("commercialAlertsSidebar");
    const handleOpen = () => setShowAlerts(true);
    const handleClose = () => setShowAlerts(false);
    alertsDrawer?.addEventListener("show.bs.offcanvas", handleOpen);
    alertsDrawer?.addEventListener("hide.bs.offcanvas", handleClose);
    return () => {
      alertsDrawer?.removeEventListener("show.bs.offcanvas", handleOpen);
      alertsDrawer?.removeEventListener("hide.bs.offcanvas", handleClose);
    };
  }, [showAlerts]);

  // Saved sidebar events
  useEffect(() => {
    const savedDrawer = document.getElementById("commercialSavedSidebar");
    const handleOpen = () => setIsSavedSidebarOpen(true);
    const handleClose = () => setIsSavedSidebarOpen(false);
    savedDrawer?.addEventListener("shown.bs.offcanvas", handleOpen);
    savedDrawer?.addEventListener("hidden.bs.offcanvas", handleClose);
    return () => {
      savedDrawer?.removeEventListener("shown.bs.offcanvas", handleOpen);
      savedDrawer?.removeEventListener("hidden.bs.offcanvas", handleClose);
    };
  }, []);

  // Fetch saved count
  useEffect(() => {
    if (isUserLoggedIn) {
      getSavedSearchAndPropertyCount(user?.user?.realbidUserEmailId || "")
        .then((data) => {
          dispatch(setSavedCount({ count: data.SAVED_SEARCH_AND_PROPERTY_COUNT }));
        })
        .catch(() => {});
    }
  }, [isUserLoggedIn, savedCount.isAddedToSaved]);

  // Sync userSubscribed state from Redux (when user provides email to save property)
  useEffect(() => {
    if (globalPopup.isUserSubscribed) {
      setUserSubscribed(true);
    } else {
      setUserSubscribed(false);
    }
  }, [globalPopup.isUserSubscribed]);

  // Check cookie on mount for persisted subscribed state
  useEffect(() => {
    const val = getCookie("subscribedUser");
    if (val === "true") {
      dispatch(setIsUserSubscribed(true));
    }
  }, []);

  // Saved tooltip ("1 item saved") after subscribing
  useEffect(() => {
    if (savedCount.isShowSavedTooltip) {
      setShowTooltip(true);
    }
  }, [savedCount.isShowSavedTooltip]);

  useEffect(() => {
    if (showTooltip) {
      setTimeout(() => {
        setShowTooltip(false);
        dispatch(showSavedTooltip(false));
      }, 2000);
    }
  }, [showTooltip]);

  // Countdown timer for sticky bid bar
  useEffect(() => {
    let endDate = latestBidCenterData?.auctionEndDate || auctionDetails?.auctionEndTime || pdpInitData?.listingDetail?.auctionEndDate || pdpInitData?.listingDetail?.listingEndTime;

    // Fallback: parse dateFormattedListingEndDate + timeFormattedListingEndTime if no timestamp available
    if (!endDate && pdpInitData?.listingDetail?.dateFormattedListingEndDate) {
      const dateStr = pdpInitData.listingDetail.dateFormattedListingEndDate; // "MM/DD/YYYY"
      const timeStr = pdpInitData.listingDetail?.timeFormattedListingEndTime || "11:59 PM";
      const parsed = new Date(`${dateStr} ${timeStr}`);
      if (!isNaN(parsed.getTime())) {
        endDate = parsed.getTime();
      }
    }

    if (!endDate) return;

    const updateTimer = () => {
      const dhm = getDHM(endDate);
      setCurrentTime(dhm);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, [latestBidCenterData?.auctionEndDate, auctionDetails?.auctionEndTime, pdpInitData?.listingDetail?.auctionEndDate, pdpInitData?.listingDetail?.listingEndTime, pdpInitData?.listingDetail?.dateFormattedListingEndDate]);

  // Compute price for sticky bid bar
  const listingType = pdpInitData?.listingDetail?.listingType;
  const status = pdpInitData?.propertyDetails?.commonAttributes?.status;
  const autoExtendConfig = parseAutoExtendConfig(pdpInitData?.propertySpecialDetails);
  const price = useMemo(() => {
    if (!pdpInitData) return null;
    return getPrice({
      listingType,
      statusPayload: {},
      bidCenterData,
      offerCenterDetail: null,
      pdpInitData,
      propCategory: pdpInitData?.propertyDetails?.commonAttributes?.propCategory,
      estimatedOpeningBid: pdpInitData?.propertyDetails?.detailedAttributes?.estimatedOpeningBid,
      auctionDetails,
    });
  }, [pdpInitData, bidCenterData, auctionDetails, listingType]);

  const handleAlertRefresh = () => {
    dispatch(setIsAlertsLoading(true));
    getAlertData()
      .then((res) => {
        dispatch(setAlerts({ ...res, alerts: [...res?.alerts] }));
        dispatch(setIsAlertsLoading(false));
      })
      .catch(() => dispatch(setIsAlertsLoading(false)));
    resetMessageCount(userID);
    getUserProfile().then((pdata) => dispatch(setProfileInfo(pdata?.data)));
  };

  const getAlertsCount = () => {
    if (isUserLoggedIn) {
      return alerts?.idologyVerificationRequired ? 1 : (alertsCount || 0);
    }
    return 1;
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    dispatch(setLoginModal(true));
    dispatch(setLoginTab("loginTab"));
    dispatch(setLoginCallLocation("Popup"));
  };

  const handleSignOut = () => {
    if (window.location.href.includes("my-shopper")) {
      setTimeout(() => { navigate("/"); userSignOut({}); }, 500);
    } else userSignOut({});
    dispatch(handleSRPLogout());
    localStorage.removeItem("recentSearch");
  };

  return (
    <>
      <header className={`commercial-header ${navbar ? "commercial-header--sticky" : ""}`}>
        <div className="commercial-header__inner">
          {/* Logo */}
          <Link to="/" className="commercial-header__logo">
            <img
              src="/assets/images/commercialPDP/commercialLogo.png"
              alt="Hubzu Commercial"
              className="commercial-header__logo-img"
            />
          </Link>

          {/* Navigation */}
          <nav className="commercial-header__nav">
            {/* Buy dropdown */}
            <div className="commercial-header__nav-item commercial-header__dropdown">
              <a href="#" className="commercial-header__nav-link">
                Buy <img src="/assets/images/pdp/dropdown.png" alt="" className="commercial-header__arrow-icon" />
              </a>
              <ul className="commercial-header__dropdown-menu">
                {buyDropDown.map((item) => (
                  <li key={item.id}>
                    <Link to={item.routerPath}>{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sell dropdown */}
            <div className="commercial-header__nav-item commercial-header__dropdown">
              <a href="#" className="commercial-header__nav-link">
                Sell <img src="/assets/images/pdp/dropdown.png" alt="" className="commercial-header__arrow-icon" />
              </a>
              <ul className="commercial-header__dropdown-menu">
                {sellDropDown.map((item) => (
                  <li key={item.id}>
                    <Link to={item.routerPath}>{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Blog */}
            <Link to="/blog" target="_blank" className="commercial-header__nav-link">
              Blog
            </Link>

            {/* Alerts */}
            <a
              href="#"
              className="commercial-header__nav-link"
              data-bs-toggle="offcanvas"
              data-bs-target="#commercialAlertsSidebar"
              aria-controls="commercialAlertsSidebar"
              onClick={() => {
                handleAlertRefresh();
                dispatch(setLoginTab(userSubscribed ? "regsiterTab" : "loginTab"));
                dispatch(setLoginCallLocation("Alerts"));
              }}
            >
              Alerts
              {getAlertsCount() !== 0 && (
                <span className="commercial-header__badge">{getAlertsCount()}</span>
              )}
            </a>

            {/* Saved - show when logged in OR subscribed (new user via email) */}
            {(isUserLoggedIn || userSubscribed) && (
              <a
                href="#"
                className="commercial-header__nav-link commercial-header__saved-link"
                data-bs-toggle="offcanvas"
                data-bs-target="#commercialSavedSidebar"
                aria-controls="commercialSavedSidebar"
                onClick={() => {
                  trackButtonClick({
                    eventName: "save_list_slider_open",
                    eventCategory: getEventCategory(),
                    eventSource: "Save slider open",
                  });
                }}
              >
                Saved
                {parseInt(savedCount?.savedCount) > 0 && (
                  <span className="commercial-header__saved-badge">{savedCount.savedCount}</span>
                )}
                {showTooltip && (
                  <span className="commercial-header__saved-tooltip">
                    1 item saved
                  </span>
                )}
              </a>
            )}
          </nav>

          {/* Right section: CTA + User */}
          <div className="commercial-header__actions">
            {/* Sell an Asset CTA */}
            {!hideSellCTA && (
              <Link to="/sell-commercial" className="commercial-header__cta">
                Sell an Asset Like This
              </Link>
            )}
            {/* User */}
            {isUserLoggedIn ? (
              <div className="commercial-header__user commercial-header__dropdown">
                <a href="#" className="commercial-header__user-trigger">
                  <span className="commercial-header__user-avatar">
                  </span>
                  <img src="/assets/images/pdp/user.png" alt="User" className="commercial-header__user-icon" />
                  <span className="commercial-header__user-name">{userName} {userLastName}</span>
                  <img src="/assets/images/pdp/dropdown.png" alt="" className="commercial-header__arrow-icon" />
                </a>
                <ul className="commercial-header__dropdown-menu commercial-header__dropdown-menu--right">
                  <UserDropDown userType={user.userType} user={user.user} />
                  <li className="signout" onClick={handleSignOut}>
                    <Link>Sign Out</Link>
                  </li>
                </ul>
              </div>
            ) : userSubscribed ? (
              <div className="commercial-header__user commercial-header__dropdown">
                <a href="#" className="commercial-header__user-trigger">
                  <span className="commercial-header__user-avatar commercial-header__user-avatar--new">
                    NU
                  </span>
                  <span className="commercial-header__user-name">New User</span>
                  <img src="/assets/images/pdp/dropdown.png" alt="" className="commercial-header__arrow-icon" />
                </a>
                <ul className="commercial-header__dropdown-menu commercial-header__dropdown-menu--right">
                  <li>
                    <p className="commercial-header__subscribe-msg">
                      Please complete your profile for a better experience
                      <button
                        className="commercial-header__complete-profile-btn"
                        data-bs-toggle="offcanvas"
                        data-bs-target="#commercialSubscribeUserProfile"
                        aria-controls="commercialSubscribeUserProfile"
                        onClick={() => {
                          dispatch(setLoginTab("regsiterTab"));
                          dispatch(setLoginCallLocation("Alerts"));
                          dispatch(setShowContinueBtn(true));
                        }}
                      >
                        Complete Your Profile
                      </button>
                    </p>
                  </li>
                </ul>
              </div>
            ) : (
              <a href="#" className="commercial-header__login" onClick={handleLoginClick}>
                Register/Sign In
              </a>
            )}

          </div>
        </div>

        {/* Breadcrumbs + Sticky Bid Bar (same row, 50-50) */}
        {!hideSellCTA && propertyDetails && (
          <div className={`commercial-header__sub-bar ${showBidHeader ? "commercial-header__sub-bar--expanded" : ""}`}>
            <div className="commercial-header__breadcrumbs">
              <div className="commercial-header__breadcrumbs-inner">
                <nav aria-label="breadcrumb">
                  <ol className="commercial-header__breadcrumb-list">
                    <li className="commercial-header__breadcrumb-item">
                      <Link to="/auctions?listingType=AUCN" target="_blank" rel="noopener noreferrer">Auctions</Link>
                    </li>
                    <li className="commercial-header__breadcrumb-item">
                      <Link to="/commercial" target="_blank" rel="noopener noreferrer">Commercial</Link>
                    </li>
                    {type && (
                      <li className="commercial-header__breadcrumb-item">
                        <Link to={`/commercial?propertySubType=${type+'_'+commonAttributes?.subType?.replace(/\s+/g, '_').toUpperCase()}`} target="_blank" rel="noopener noreferrer">{type.split(/[_\s]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}</Link>
                      </li>
                    )}
                    {stateName && (
                      <li className="commercial-header__breadcrumb-item">
                        <Link to={address?.stateLandingUrl || `/commercial`} target="_blank" rel="noopener noreferrer">{stateName}</Link>
                      </li>
                    )}
                    <li className="commercial-header__breadcrumb-item commercial-header__breadcrumb-item--active">
                      {propertyName || `${address?.streetNumber || ""} ${address?.streetName || ""}`.trim()}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>

            {pdpInitData && (
              <div className={`commercial-header__bid-bar ${showBidHeader ? "commercial-header__bid-bar--visible" : ""}`}>
                <BidCenterHeader
                  statusPayload={{}}
                  pdpBidCenter={{}}
                  listingType={listingType}
                  status={status}
                  offerType={null}
                  offerCenterDetail={null}
                  isSignedUser={isUserLoggedIn}
                  isFcOnlineFlag={false}
                  pdpData={pdpInitData}
                  financialType={pdpInitData?.listingDetail?.financialType || []}
                  pdpPropertyFees={null}
                  isWatchProperty={false}
                  priceLabel={price?.label}
                  priceAmount={price?.amount}
                  timeLeft={currentTime?.days || currentTime?.hours || currentTime?.minutes || 0}
                  timeLeftUnit={currentTime?.days ? "days" : currentTime?.hours ? "hours" : currentTime?.minutes ? "mins" : "days"}
                  isLastFewMins={currentTime && (!currentTime?.days && !currentTime?.hours && currentTime?.minutes) && (currentTime?.minutes <= autoExtendConfig.autoExtendWindow)}
                />
              </div>
            )}
          </div>
        )}
      </header>

      {/* Alerts Offcanvas */}
      <div
        className="offcanvas offcanvas-end offcanvas-listing-sidebar"
        id="commercialAlertsSidebar"
        style={{ width: "50%" }}
        data-bs-scroll="true"
        data-bs-backdrop="false"
      >
        <div className="offcanvas-header">
          <CgBell size={38} color="#2A69A3" />
          <h1 className="offcanvas-title title-txt">Alerts</h1>
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>
        <div className="offcanvas-body">
          <div className="sidebar_listing_list pt0">
            {loading ? (
              <div className="text-center p-4 mt10">
                <div className="mt30 pt30">
                  {[1, 2].map((item) => (
                    <div key={item} className="w-100 mb-3">
                      <ShimmerContentBlock cta card text thumbnailWidth={130} thumbnailHeight={100} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <Alerts />
            )}
          </div>
        </div>
      </div>

      {/* Saved Offcanvas */}
      <div
        className="offcanvas offcanvas-end offcanvas-listing-sidebar"
        id="commercialSavedSidebar"
        tabIndex="-1"
        style={{ width: "500px", maxWidth: "450px" }}
        data-bs-scroll="true"
        data-bs-backdrop="false"
      >
        {isSavedSidebarOpen && <SavedPreference />}
      </div>
      {/* SubscribedUser Offcanvas — Complete Profile for new subscribed users */}
      <div
        className="offcanvas offcanvas-end offcanvas-listing-sidebar"
        id="commercialSubscribeUserProfile"
        data-bs-scroll="true"
        data-bs-backdrop="false"
      >
        <SubscribedUser />
      </div>
    </>
  );
};

export default CommercialHeader;
